import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Lock, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import MaskedLines from "../components/MaskedLines";
import Reveal from "../components/Reveal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuth } from "../lib/auth";
import api from "../lib/api";

export default function Portal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const [lo, setLo] = useState({
    name: "", email: "", phone: "", location: "", acreage: "",
    title_status: "Clear", model: "JV", notes: "",
  });
  const [inv, setInv] = useState({
    name: user?.name || "", email: user?.email || "", phone: "",
    ticket_size: "₹1 - 5 Cr", interest: "Distress Flips", notes: "",
  });

  const submitLo = async (e) => {
    e.preventDefault();
    if (!/^[+0-9 \-()]{7,20}$/.test(lo.phone)) return toast.error("Enter a valid phone");
    if (!lo.acreage || Number(lo.acreage) <= 0) return toast.error("Enter valid acreage");
    try {
      await api.post("/portal/landowner", { ...lo, acreage: Number(lo.acreage) });
      toast.success("Submitted. Our JV desk will respond within 3 business days.");
      setLo({ name: "", email: "", phone: "", location: "", acreage: "", title_status: "Clear", model: "JV", notes: "" });
    } catch { toast.error("Failed to submit"); }
  };
  const submitInv = async (e) => {
    e.preventDefault();
    if (!user) return nav("/login?next=/portal");
    try {
      await api.post("/portal/investor", inv);
      toast.success("Access request received. Investor Desk credentials will be issued within 24 hours.");
    } catch { toast.error("Failed to submit"); }
  };

  return (
    <div className="pt-[112px]">
      <section className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="eyebrow mb-6 flex items-center gap-3"><span className="accent-dot" /> Dual Portal · Landowners × Investors</div>
        <h1 className="display-1 max-w-6xl">
          <MaskedLines lines={["Two doors. Same", "underwriting desk."]} />
        </h1>
        <p className="mt-10 max-w-2xl text-[15px] leading-[1.8] text-[#EDE6D3]/75">
          Landowners submit parcels for JV or outright acquisition. Investors apply for private access to pre-vetted SARFAESI auctions and distress-priced flips.
        </p>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-20" data-testid="portal-section">
        <Tabs defaultValue="landowner">
          <TabsList className="w-full flex bg-transparent border-b border-[#EDE6D3]/10 rounded-none p-0 h-auto justify-start gap-0">
            <TabsTrigger value="landowner" data-testid="tab-landowner" className="rounded-none py-5 px-6 text-[11px] tracking-[2px] uppercase data-[state=active]:bg-[#EDE6D3] data-[state=active]:text-[#0A2342] data-[state=active]:shadow-none">
              01 · Landowner Portal
            </TabsTrigger>
            <TabsTrigger value="investor" data-testid="tab-investor" className="rounded-none py-5 px-6 text-[11px] tracking-[2px] uppercase data-[state=active]:bg-[#EDE6D3] data-[state=active]:text-[#0A2342] data-[state=active]:shadow-none">
              02 · Investor Desk
            </TabsTrigger>
          </TabsList>

          <TabsContent value="landowner" className="pt-14">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
              <Reveal className="md:col-span-5">
                <div className="chapter-num mb-3">§ Landowner</div>
                <h2 className="font-serif text-[38px] md:text-[52px] leading-tight tracking-tight">Submit your parcel.</h2>
                <p className="mt-6 text-[14px] leading-[1.8] text-[#EDE6D3]/70">Every submission runs through a title audit, feasibility study and financial model — no obligation, no cost, no listing on public boards.</p>
                <ul className="mt-10 space-y-3 text-[13px] text-[#EDE6D3]/80">
                  <li>— Free 3-day feasibility study</li>
                  <li>— 60/40 default DM split (negotiable)</li>
                  <li>— No public listing; strict NDA</li>
                </ul>
              </Reveal>

              <form onSubmit={submitLo} className="md:col-span-7 card-sand p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6" data-testid="landowner-form">
                <div><div className="bb-label">Name</div><input required className="bb-input" value={lo.name} onChange={(e) => setLo({ ...lo, name: e.target.value })} data-testid="lo-name" /></div>
                <div><div className="bb-label">Email</div><input required type="email" className="bb-input" value={lo.email} onChange={(e) => setLo({ ...lo, email: e.target.value })} data-testid="lo-email" /></div>
                <div><div className="bb-label">Phone</div><input required className="bb-input" value={lo.phone} onChange={(e) => setLo({ ...lo, phone: e.target.value })} data-testid="lo-phone" placeholder="+91 98xxxxxxxx" /></div>
                <div><div className="bb-label">Location</div><input required className="bb-input" value={lo.location} onChange={(e) => setLo({ ...lo, location: e.target.value })} data-testid="lo-location" placeholder="Devanahalli, Bengaluru" /></div>
                <div><div className="bb-label">Acreage</div><input required type="number" step="0.1" className="bb-input" value={lo.acreage} onChange={(e) => setLo({ ...lo, acreage: e.target.value })} data-testid="lo-acreage" /></div>
                <div><div className="bb-label">Title Status</div>
                  <select className="bb-input" value={lo.title_status} onChange={(e) => setLo({ ...lo, title_status: e.target.value })} data-testid="lo-title">
                    <option>Clear</option><option>Under Verification</option><option>Disputed</option>
                  </select>
                </div>
                <div className="md:col-span-2"><div className="bb-label">Expected Model</div>
                  <div className="grid grid-cols-3 gap-3 mt-2">
                    {["JV", "Outright Sale", "Development Rights"].map((m) => (
                      <button type="button" key={m} onClick={() => setLo({ ...lo, model: m })} data-testid={`lo-model-${m.replace(/ /g, '')}`}
                        className={`text-[11px] tracking-[2px] uppercase py-3 border ${lo.model === m ? "bg-[#EDE6D3] text-[#0A2342] border-[#EDE6D3]" : "border-[#EDE6D3]/20 hover:border-[#D4AF37]"}`}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2"><div className="bb-label">Notes</div>
                  <textarea rows={3} className="bb-input" value={lo.notes} onChange={(e) => setLo({ ...lo, notes: e.target.value })} data-testid="lo-notes" />
                </div>
                <button type="submit" className="btn-primary md:col-span-2 justify-center" data-testid="lo-submit">Submit Parcel <ArrowRight size={14} /></button>
              </form>
            </div>
          </TabsContent>

          <TabsContent value="investor" className="pt-14">
            {!user ? (
              <Reveal>
                <div className="card-sand p-10 md:p-16 text-center max-w-2xl mx-auto" data-testid="investor-locked">
                  <div className="mx-auto w-14 h-14 border border-[#D4AF37] flex items-center justify-center mb-6">
                    <Lock size={20} color="#D4AF37" />
                  </div>
                  <div className="eyebrow">Access Restricted</div>
                  <h3 className="mt-4 font-serif text-[36px] md:text-[44px] leading-tight tracking-tight">The Investor Desk is a private room.</h3>
                  <p className="mt-6 text-[13px] leading-[1.8] text-[#EDE6D3]/70 max-w-md mx-auto">Log in or register to submit an access request for pre-vetted SARFAESI auctions and distress-priced flips.</p>
                  <div className="mt-8 flex justify-center gap-3">
                    <button className="btn-primary" onClick={() => nav("/login?next=/portal")} data-testid="investor-login">Login</button>
                    <button className="btn-outline" onClick={() => nav("/register?next=/portal")} data-testid="investor-register">Register</button>
                  </div>
                </div>
              </Reveal>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                <Reveal className="md:col-span-5">
                  <div className="chapter-num mb-3">§ Investor Desk</div>
                  <h2 className="font-serif text-[38px] md:text-[52px] leading-tight tracking-tight">Signed in as <span className="text-[#D4AF37]">{user.name}</span>.</h2>
                  <p className="mt-6 text-[14px] leading-[1.8] text-[#EDE6D3]/70">Submit your interest below. Our SARFAESI cell will confirm eligibility and issue a private link to the live pipeline within 24 hours.</p>
                  <div className="mt-10 space-y-3 text-[13px] text-[#EDE6D3]/80">
                    <li className="list-none">— Pre-NCLT bank auction access</li>
                    <li className="list-none">— 30-45% below market entry</li>
                    <li className="list-none">— Institutional-grade due diligence</li>
                  </div>
                  <button onClick={logout} className="mt-10 text-[11px] tracking-[2px] uppercase text-[#EDE6D3]/60 flex items-center gap-2 hover:text-[#D4AF37]"><LogOut size={12} /> Sign out</button>
                </Reveal>
                <form onSubmit={submitInv} className="md:col-span-7 card-sand p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6" data-testid="investor-form">
                  <div><div className="bb-label">Name</div><input required className="bb-input" value={inv.name} onChange={(e) => setInv({ ...inv, name: e.target.value })} data-testid="inv-name" /></div>
                  <div><div className="bb-label">Email</div><input required type="email" className="bb-input" value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} data-testid="inv-email" /></div>
                  <div><div className="bb-label">Phone</div><input required className="bb-input" value={inv.phone} onChange={(e) => setInv({ ...inv, phone: e.target.value })} data-testid="inv-phone" /></div>
                  <div><div className="bb-label">Ticket Size</div>
                    <select className="bb-input" value={inv.ticket_size} onChange={(e) => setInv({ ...inv, ticket_size: e.target.value })} data-testid="inv-ticket">
                      <option>Under ₹1 Cr</option><option>₹1 - 5 Cr</option><option>₹5 - 20 Cr</option><option>₹20 Cr +</option>
                    </select>
                  </div>
                  <div className="md:col-span-2"><div className="bb-label">Interest</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                      {["SARFAESI Auctions", "Distress Flips", "Commercial Yield", "Land Banks"].map((s) => (
                        <button type="button" key={s} onClick={() => setInv({ ...inv, interest: s })} data-testid={`inv-int-${s.replace(/ /g, '')}`}
                          className={`text-[10px] tracking-[2px] uppercase py-3 border ${inv.interest === s ? "bg-[#EDE6D3] text-[#0A2342] border-[#EDE6D3]" : "border-[#EDE6D3]/20 hover:border-[#D4AF37]"}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="md:col-span-2"><div className="bb-label">Notes</div>
                    <textarea rows={3} className="bb-input" value={inv.notes} onChange={(e) => setInv({ ...inv, notes: e.target.value })} data-testid="inv-notes" />
                  </div>
                  <button type="submit" className="btn-primary md:col-span-2 justify-center" data-testid="inv-submit">Request Access <ArrowRight size={14} /></button>
                </form>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}
